import { HttpVerb } from '../../types/api-types'
import { Token, AuthMethod } from '../../types/user-types'
import { ErrorCode } from '../components/errors/constants'
import { isValidToken } from './auth-utils'
import Cookies from 'js-cookie'

type FetchApiProps = {
  url: string
  token: Token
  method?: HttpVerb
  body?: string | FormData
  additionalHeaders?: HeadersInit
  abortSignal?: AbortSignal
  auth_method?: AuthMethod
}

export function getApiEndpoint() {
  const apiEndpoint = process.env.REACT_APP_SERVER_ADDRESS
  if (!apiEndpoint) throw Error('NO_API_ENDPOINT')
  return apiEndpoint.endsWith('/') ? apiEndpoint : apiEndpoint + '/'
}

export function getHeaders(auth_method: AuthMethod, token: Token, additionalHeaders?: HeadersInit): HeadersInit {
  let headers: HeadersInit = {
    Accept: 'application/json',
    Referer: 'origin',
  }

  if (isValidToken(token)) {
    headers['Authorization'] = `${auth_method} ${token}`
  }

  if (additionalHeaders) {
    headers = { ...headers, ...additionalHeaders }
  }

  return headers
}

export default async function fetchApi({
  url,
  token,
  auth_method = 'Bearer',
  method = HttpVerb.GET,
  body,
  additionalHeaders,
  abortSignal,
}: FetchApiProps) {
  let apiUrl = `${getApiEndpoint()}api${url.startsWith('/') ? url : '/' + url}`

  let headers: HeadersInit = getHeaders(auth_method, token, additionalHeaders)

  if (!(body instanceof FormData)) {
    headers = { 'content-type': 'application/json', ...headers }
  }

  // Send CSRF token to server, also requires "credentials: 'include'" to send/set cookies
  if (method !== HttpVerb.GET) {
    const csrfToken = Cookies.get('csrftoken') || ''
    headers = { ...headers, 'X-CSRFToken': csrfToken }
  }

  const options = { method, headers, body, signal: abortSignal, credentials: 'include' as RequestCredentials }
  const response = await fetch(apiUrl, options)

  if (!response.ok) {
    switch (response.status) {
      case 400:
        // 400 responses are caught and displayed as toast messages
        // inside `useEditForm` hook
        const result = await response.json()
        throw result
      case 401:
        throw new Error(ErrorCode.SESSION_TIMEOUT)
      case 403:
        throw new Error(ErrorCode.PERMISSION_DENIED)
      case 404:
        throw new Error(ErrorCode.NOT_FOUND)
      default:
        throw new Error(ErrorCode.SERVER_ERROR)
    }
  }

  return response.json()
}

export function getLookupData(
  fieldId: string,
  token: Token,
  auth_method?: AuthMethod,
  fieldFilters?: Record<string, string | number | boolean | null>,
) {
  let filters = ''
  if (fieldFilters) {
    filters = Object.keys(fieldFilters!)
      .map((key) => `${key}=${fieldFilters[key]}`)
      .join('&')
  }

  const url = !fieldFilters ? `lookup/?field=${fieldId}` : `lookup/?field=${fieldId}&${filters}`
  return fetchApi({
    url,
    token,
    auth_method,
  })
}

export function getWellHeaderData(wellId: string, token: Token, auth_method?: AuthMethod) {
  const url = `wells/${wellId}/header`
  return fetchApi({
    url,
    token,
    auth_method,
  })
}

export function getAddressSuggestionsEsri(
  query: string,
  addressType: string = 'All',
  token: Token,
  auth_method?: AuthMethod,
) {
  const url = `esri-address/suggest/?q=${encodeURIComponent(query)}&type=${encodeURIComponent(addressType)}`
  return fetchApi({
    url,
    token,
    auth_method,
  })
}
